import { createContext, useContext, useCallback, useMemo, useState, useRef, type ReactNode } from 'react'
import type { MiniappIconSpec, FolderColorKey } from '@papan/types'
import {
  EMPTY_DOCK, pinInDock, unpinFromDock, groupInDock, moveInDock, updateFolderInDock, removeFolderFromDock, setIconInDock, setPinTitleInDock, reorderDock, reorderFolderInDock,
  type FolderPatch,
  type Dock,
} from './rail/dock.js'

/**
 * Shell app-instance manager — the OS-like taskbar/dock model.
 *
 * Every taskbar surface is an "app" the shell can run. A running app stays
 * MOUNTED in the app deck (App.tsx) with its window display-toggled by the
 * active route — the same keep-alive pattern WorkspaceCache proves — so
 * switching apps preserves scroll/selection/editor state instead of
 * remounting a route element.
 *
 * Two families (docs/plans/2026-07-05_OS_SHELL_REDESIGN.md):
 * - System apps ('drive' | 'workspace' | 'keybook' | 'settings' | 'launcher' | 'apps')
 *   — singleton, ref is always ''.
 * - User apps ('miniapp' | 'html') — carry a ref (the hosted file/folder id);
 *   several can run at once, keyed by (kind, ref).
 *
 * This provider is intentionally feature-agnostic: it tracks WHICH apps run,
 * never what they render. App.tsx owns the per-kind rendering, so core-ui
 * keeps zero feature imports.
 */
export type ShellAppKind = 'drive' | 'workspace' | 'chat' | 'keybook' | 'settings' | 'billing' | 'launcher' | 'apps' | 'miniapp' | 'html'

export interface ShellAppInstance {
  kind: ShellAppKind
  /** '' for system apps; hosted fileId/folderId for user apps. */
  ref: string
  /** Dock label for user apps (file name). Updatable after a lazy resolve. */
  title?: string
  /**
   * Mini-app icon spec for the dock chip (null/undefined → the dock renders
   * the name-derived default). Set at open time from the originating row's
   * metadata_json; updatable after a lazy resolve.
   */
  icon?: MiniappIconSpec | null
  /** Cache-bust rev paired with `ref` for an IMAGE icon. Without it the dock chip
   *  can only draw the name-derived gradient — the spec alone can't fetch the
   *  composited PNG. Travels with `icon` everywhere. */
  iconRev?: string | null
}

/** A pinned user app — stays on the dock even when not running (Phase 2b). */
export interface ShellPin {
  kind: ShellAppKind
  ref: string
  title?: string
  /** Mini-app icon spec for the dock chip. Live-updated by `setAppIcon` on a
   *  realtime `file:updated`; not persisted (hydrated pins start undefined →
   *  name-derived default until the app runs or its icon changes). */
  icon?: MiniappIconSpec | null
  /** Cache-bust rev paired with `ref`; see ShellAppInstance.iconRev. */
  iconRev?: string | null
  /** Rail slot — the server's `pin_order`. Dock order sorts pins AND folders
   *  on this together (rail/composeRailEntries.ts); a pin minted locally takes
   *  the next free slot, which is what the server's count-based order yields
   *  too, and the next hydrate is authoritative either way. */
  pinOrder: number
  /** The folder this app is filed in, or null on the rail itself (0195). */
  folderId: string | null
}

/** A taskbar app folder (0195) — a group of pins drawn as ONE rail tile.
 *  Holds no member list of its own: membership is `ShellPin.folderId`, the
 *  same shape as the database, so hydration is a copy rather than a join. */
export interface ShellFolder {
  id: string
  /** '' = unnamed; the tile shows the localised default. */
  title: string
  pinOrder: number
  /** A palette key (0196), or null for the default glyph background. */
  color: FolderColorKey | null
}

const instanceKey = (kind: ShellAppKind, ref: string) => `${kind}:${ref}`

interface ShellManagerValue {
  /** Insertion-ordered running (mounted, keep-alive) app instances. */
  runningApps: ShellAppInstance[]
  isRunning: (kind: ShellAppKind, ref?: string) => boolean
  /** Idempotent — marks an instance running so its deck window mounts.
   *  Re-opening an existing instance updates its title/icon if given. */
  openApp: (kind: ShellAppKind, ref?: string, title?: string, icon?: MiniappIconSpec | null, iconRev?: string | null) => void
  /** Removes an instance from the running set (its window unmounts, state is lost). */
  closeApp: (kind: ShellAppKind, ref?: string) => void
  /** Live-update a user app's dock icon (running AND pinned entries) in place —
   *  no-op if the ref isn't open/pinned. Driven by realtime `file:updated` so
   *  an icon edit refreshes the taskbar chip without a reload. */
  setAppIcon: (kind: ShellAppKind, ref: string, icon: MiniappIconSpec | null, iconRev?: string | null) => void
  /** Drop all running apps except `keep` — e.g. on tenant switch, where
   *  every window's content (and any frozen location) is tenant-scoped. */
  resetApps: (keep?: ShellAppInstance | null) => void

  // ── Pins + folders — persistence-free/generic; the network lives in
  //    useShellPersistence, which hydrates via hydrateDock and PATCHes on
  //    every mutation. Each mutator here is the LOCAL mirror of one route.
  /** Pin-ordered user apps kept on the dock regardless of running-state. */
  pinnedApps: ShellPin[]
  /** Folders (0195), in rail order. A folder with no members never survives
   *  a mutation here — the client half of the repository's rule. */
  folders: ShellFolder[]
  isPinned: (kind: ShellAppKind, ref?: string) => boolean
  /** Idempotent local pin (updates title if given). */
  pinApp: (kind: ShellAppKind, ref?: string, title?: string) => void
  /** Local unpin; prunes the folder it was the last member of. */
  unpinApp: (kind: ShellAppKind, ref?: string) => void
  /** Replace BOTH lists at once — hydration and tenant-switch reset. Two
   *  setters would give one render in which pins name folders that are not
   *  there yet. */
  hydrateDock: (pins: ShellPin[], folders: ShellFolder[]) => void
  /** Mirror of POST /folders: adds the folder and files `members` in it.
   *  Members that are not pinned are ignored — pin first, as the server does. */
  groupPins: (folder: ShellFolder, members: Array<{ kind: ShellAppKind; ref: string }>) => void
  /** Mirror of PATCH /pins/folder. `null` = back to the rail. */
  movePinToFolder: (kind: ShellAppKind, ref: string, folderId: string | null) => void
  /** Mirror of PATCH /folders/:id — title and/or colour. */
  updateFolder: (id: string, patch: FolderPatch) => void
  /** Refresh a pinned app's title (a rename arrived); no-op when not pinned. */
  setPinTitle: (kind: ShellAppKind, ref: string, title: string) => void
  /** Mirror of DELETE /folders/:id — the members return to the rail. */
  removeFolder: (id: string) => void
  /** Mirror of PUT /rail: the rail's top-level order as keys
   *  (`app:<kind>:<ref>` / `folder:<id>`); each takes pinOrder = index. */
  reorderRail: (keys: string[]) => void
  /** Mirror of PUT /folders/:id/order: the member order inside one folder. */
  reorderFolder: (folderId: string, keys: string[]) => void
}

const ShellManagerContext = createContext<ShellManagerValue | null>(null)

export function ShellManagerProvider({ children }: { children: ReactNode }) {
  const [runningApps, setRunningApps] = useState<ShellAppInstance[]>([])
  // Pins and folders live in ONE state atom so a mutation that touches both
  // (a folder emptied by an unpin, a folder removed and its members freed)
  // is one render, never a frame of pins pointing at a folder that is gone.
  const [dock, setDock] = useState<Dock>(EMPTY_DOCK)

  const isRunning = useCallback(
    (kind: ShellAppKind, ref = '') => runningApps.some(a => a.kind === kind && a.ref === ref),
    [runningApps],
  )

  const openApp = useCallback((kind: ShellAppKind, ref = '', title?: string, icon?: MiniappIconSpec | null, iconRev?: string | null) => {
    setRunningApps(prev => {
      const existing = prev.find(a => instanceKey(a.kind, a.ref) === instanceKey(kind, ref))
      if (existing) {
        const nextTitle = title && existing.title !== title ? title : existing.title
        const nextIcon = icon !== undefined ? icon : existing.icon
        const nextRev = icon !== undefined ? (iconRev ?? null) : existing.iconRev
        if (nextTitle !== existing.title || nextIcon !== existing.icon || nextRev !== existing.iconRev) {
          return prev.map(a => (a === existing ? { ...a, title: nextTitle, icon: nextIcon, iconRev: nextRev } : a))
        }
        return prev
      }
      return [...prev, { kind, ref, title, icon, iconRev: iconRev ?? null }]
    })
  }, [])

  const closeApp = useCallback((kind: ShellAppKind, ref = '') => {
    setRunningApps(prev => prev.filter(a => !(a.kind === kind && a.ref === ref)))
  }, [])

  const setAppIcon = useCallback((kind: ShellAppKind, ref: string, icon: MiniappIconSpec | null, iconRev?: string | null) => {
    setRunningApps(prev => {
      const i = prev.findIndex(a => a.kind === kind && a.ref === ref)
      if (i < 0) return prev
      const next = [...prev]
      next[i] = { ...next[i], icon, iconRev: iconRev ?? null }
      return next
    })
    setDock(prev => setIconInDock(prev, kind, ref, icon, iconRev ?? null))
  }, [])

  const resetApps = useCallback((keep?: ShellAppInstance | null) => {
    setRunningApps(keep ? [keep] : [])
  }, [])

  const isPinned = useCallback(
    (kind: ShellAppKind, ref = '') => dock.pins.some(p => p.kind === kind && p.ref === ref),
    [dock.pins],
  )
  // A running instance carries the app's live icon; the pin, freshly created,
  // carries none — and once the app closes the pin is all that is left, so the
  // tile would drop to the name-derived default the moment you close what you
  // just pinned. Seed it here. (Hydration resolves the icon for pins made in
  // an earlier session — GET /api/shell/pins reads it off the app folder.)
  const runningRef = useRef(runningApps)
  runningRef.current = runningApps
  const pinApp = useCallback((kind: ShellAppKind, ref = '', title?: string) => {
    const live = runningRef.current.find(a => a.kind === kind && a.ref === ref)
    setDock(prev => {
      const next = pinInDock(prev, kind, ref, title)
      return live?.icon === undefined ? next : setIconInDock(next, kind, ref, live.icon, live.iconRev ?? null)
    })
  }, [])
  const unpinApp = useCallback((kind: ShellAppKind, ref = '') => {
    setDock(prev => unpinFromDock(prev, kind, ref))
  }, [])
  const hydrateDock = useCallback((pins: ShellPin[], folders: ShellFolder[]) => {
    setDock({ pins, folders })
  }, [])
  const groupPins = useCallback((folder: ShellFolder, members: Array<{ kind: ShellAppKind; ref: string }>) => {
    setDock(prev => groupInDock(prev, folder, members))
  }, [])
  const movePinToFolder = useCallback((kind: ShellAppKind, ref: string, folderId: string | null) => {
    setDock(prev => moveInDock(prev, kind, ref, folderId))
  }, [])
  const updateFolder = useCallback((id: string, patch: FolderPatch) => {
    setDock(prev => updateFolderInDock(prev, id, patch))
  }, [])
  const setPinTitle = useCallback((kind: ShellAppKind, ref: string, title: string) => {
    setDock(prev => setPinTitleInDock(prev, kind, ref, title))
  }, [])
  const removeFolder = useCallback((id: string) => {
    setDock(prev => removeFolderFromDock(prev, id))
  }, [])
  const reorderRail = useCallback((keys: string[]) => {
    setDock(prev => reorderDock(prev, keys))
  }, [])
  const reorderFolder = useCallback((folderId: string, keys: string[]) => {
    setDock(prev => reorderFolderInDock(prev, folderId, keys))
  }, [])

  const value = useMemo(
    () => ({
      runningApps, isRunning, openApp, closeApp, setAppIcon, resetApps,
      pinnedApps: dock.pins, folders: dock.folders,
      isPinned, pinApp, unpinApp, hydrateDock, groupPins, movePinToFolder, updateFolder, setPinTitle, removeFolder, reorderRail, reorderFolder,
    }),
    [runningApps, isRunning, openApp, closeApp, setAppIcon, resetApps, dock, isPinned, pinApp, unpinApp, hydrateDock, groupPins, movePinToFolder, updateFolder, setPinTitle, removeFolder, reorderRail, reorderFolder],
  )

  return <ShellManagerContext.Provider value={value}>{children}</ShellManagerContext.Provider>
}

export function useShell(): ShellManagerValue {
  const ctx = useContext(ShellManagerContext)
  if (!ctx) throw new Error('useShell must be used within ShellManagerProvider')
  return ctx
}
